import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Le sol est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'shockwave',
  family: 'geometric',
  danceability: 0.88,
  requiresTier: 0,
  energyLevel: 0.86,
  bpmMin: 120,
  bpmMax: 175,
  movement: 0.80,
  tonality: 0.35,
  prefers: { lowEnd: [0.55, 1.0] },
  enabled: true
};

// Shockwave : un sol noir brillant à perte de vue ; chaque kick envoie une
// onde de choc circulaire qui court sur le sol, déforme ses reflets et
// soulève de la poussière lumineuse. Techno, bass music, dubstep, hard
// techno. Règles de kiosk-app/CLAUDE.md (R1 à R11), rythme « party » (R9 :
// danceability 0,88).
//
// TROIS COUCHES, UN DRAW CALL CHACUNE, AUCUN UPLOAD D'ATTRIBUT PAR IMAGE :
//  1. LE SOL - un maillage en trapèze qui suit le champ de la caméra (rangs
//     espacés en exponentielle jusqu'à 600 unités), déplacé dans le vertex
//     shader en mode DUNES (relief lisse, normales ANALYTIQUES, R5). Tout le
//     reste est dans le fragment shader, par pixel :
//     - la GRILLE, chaque trait rendu par sa distance en PIXELS (R1 : coeur
//       1 px, gaine, halo) ; quand une maille passe sous ~3 px vers
//       l'horizon, les traits se fondent dans leur densité moyenne - la
//       grille reste nette sans moiré ni scintillement ;
//     - les ONDES (8 en uniforms : centre, rayon, force) : une crête fine
//       (R1, couleur d'accent), et la dérivée d'une bosse gaussienne
//       centrée sur la crête qui DÉCALE radialement les coordonnées de la
//       grille (réfraction) et PENCHE la normale du miroir - l'onde se voit
//       comme une distorsion qui traverse le sol ;
//     - le REFLET des barres par MIROIR ANALYTIQUE : le rayon de vue réfléchi
//       sur la normale (dunes + ondes) est intersecté avec le plan des
//       barres, et la distance aux barres y est évaluée - aucune cible de
//       rendu.
//     Les coordonnées du monde montent à 600 unités : fragment en HIGHP
//     (exception documentée - en mediump, fp16 sur la télé, fract() d'une
//     coordonnée de 600 n'a plus que 0,25 de résolution et la grille casse).
//  2. LES BARRES - une rangée de barres néon horizontales à l'horizon, sur
//     un quad du plan z = BAR_Z, en distance en pixels (R1), additives.
//  3. LA POUSSIÈRE - 4 000 points de 1 px (R7) posés au sol (répartis
//     uniformément À L'ÉCRAN sous l'horizon), soulevés par la crête qui
//     passe puis retombant : hauteur = enveloppe analytique de chaque onde à
//     leur position, dans le vertex shader. (Pas d'accumulation temporelle :
//     R7 la réserve aux objets lents, la poussière soulevée est rapide.)
// Au-dessus de l'horizon : noir pur (R3), les barres seules.

const TIER = {
  0: { dust: 2000, waves: 4, rows: 48, cols: 40 },
  1: { dust: 4000, waves: 8, rows: 64, cols: 48 },
  2: { dust: 4000, waves: 8, rows: 64, cols: 48 }
};
// la caméra : légère plongée, l'horizon à 50 % au-dessus du centre de l'image
export const CAM_H = 4.0;
export const PITCH = Math.atan(0.5 * Math.tan(THREE.MathUtils.degToRad(34)));   // 18,6 degrés
const FOV = 68, FAR = 900;
const FLOOR_FAR = 600, FLOOR_NEAR = 2.0;
const CELL = 1.0;                         // la maille de la grille, en unités
export const BAR_Z = -150;
const N_BARS = 7;
const BAR_Y_MAX = 16.5;                  // le haut de la plus haute barre (3,5 + 13)
// les ondes
export const CREST_W = 0.9;               // la largeur de la crête (unités) : c(r) = exp(-((r - R) / W)^2)
export const RIPPLE_AMP = 0.37;           // déplacement max ~0,35 maille (c' max = 0,86 / W)
export const LIFT_AHEAD = 0.5, LIFT_BEHIND = 3.2, LIFT_MAX = 1.1;
const LIFE_BEATS = 2;
const BEAT_R = 30, BAR_R = 95, DROP_R = 140;   // rayon atteint en fin de vie
const REFRACT = 1.0;
const GRID_BASE = 1.0;                    // facteur de la grille (sa couleur porte déjà la luminance max 0,35)
const GRID_LUM = 0.34;                    // couleur de palette atténuée : accent (L 0,55) x 0,45 -> luminance <= 0,35
const RING_CORE = 1.5;                   // l'anneau : le seul coeur au-dessus des barres (critère 5)
const BAR_CORE = 0.85;                    // les barres : coeur blanc au-dessus du seuil de bloom, sous l'anneau
const BAR_HALO_PX = 14;                   // halo des barres exp(-d / 14) x 0,35, nul à 6 largeurs (84 px)
const GLOW_MAX = 0.05;                    // la lueur au-dessus de l'horizon (<= 0,06)
const REFL_GAIN = 0.45, REFL_LEN = 0.25;
const REFL_SPAN = 220, REFL_TEX = 1024;   // la texture du profil : abscisses -220..220 du plan des barres  // le reflet : traînée verticale, longueur 25 % de la hauteur du sol
const GRID_FLASH_TAU = 0.12, BARS_WHITE_BEATS = 4;
const DUNE_AMP = 1.1;
const SAFE_FLOOR = 0.45;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}
function rng(seed) { let s = seed >>> 0; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }

// L'ENVELOPPE DE LA POUSSIÈRE (copie JS exacte du vertex shader, pour les
// tests) : d = distance à la crête (> 0 devant, la crête n'est pas encore
// passée ; < 0 derrière). Devant : la montée, raide (LIFT_AHEAD) ; derrière :
// la retombée, longue (LIFT_BEHIND) - la poussière monte au passage de la
// crête puis retombe.
export function enveloppe(d) {
  const w = d >= 0 ? LIFT_AHEAD : LIFT_BEHIND;
  return Math.exp(-(d / w) * (d / w));
}
// les dunes (copie JS du vertex shader) : hauteur et gradient
export function dunes(x, z) {
  const fade = Math.exp(Math.max(0, -z - 60) / -90);
  const a = 0.21 * x + 0.13 * z, b = 0.37 * z - 0.11 * x + 1.3, c = 0.07 * x + 0.05 * z + 2.1;
  const h = DUNE_AMP * fade * (0.55 * Math.sin(a) + 0.30 * Math.sin(b) + 0.35 * Math.sin(c));
  return h;
}

// --------------------------------------------------------------- les shaders

const SAFE_CHUNK = `
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAtt(vec2 ndc) {
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;
// R1 : le profil d'un trait selon sa distance en PIXELS. coeur 0-2 px
// blanc chaud (le seul au-dessus de 0,78), gaine 2-6 px couleur à 0,7,
// halo exp(-d / 10) x 0,35. coreW règle la largeur du coeur (1 px pour la
// grille et les anneaux, 2 px pour les barres).
const LINE_CHUNK = `
  // coreCol : le coeur (blanc chaud pour ce qui doit fleurir - crêtes,
  // barres, grille pendant le flash ; la couleur pour la grille au repos) ;
  // haloG : 1 pour le halo complet de R1, 0 pour un trait sec (la grille :
  // des milliers de halos superposés soulevaient tout le sol en rose)
  vec3 traitCore(float core, float sheath, float dPx, vec3 coreCol, vec3 col, float haloG) {
    // le halo de R1, ramené à ZÉRO au-delà de 40 px : sa traîne (0,002 à
    // 60 px) ressort à 10/255 après ACES et sRGB - un voile coloré sur le noir.
    // Halo de la COULEUR du trait, jamais gris.
    float halo = exp(-dPx / 10.0) * 0.35 * (1.0 - smoothstep(20.0, 40.0, dPx)) * haloG;
    return coreCol * core + col * 0.7 * max(sheath - core, 0.0) + col * halo * (1.0 - sheath);
  }
  vec3 trait(float dPx, vec3 coreCol, vec3 col, float coreW, float haloG) {
    float core = 1.0 - smoothstep(coreW - 0.5, coreW + 0.5, dPx);
    float sheath = 1.0 - smoothstep(coreW + 1.5, coreW + 4.5, dPx);
    return traitCore(core, sheath, dPx, coreCol, col, haloG);
  }
  // la grille : 1 px de coeur, 1 px de gaine - « fine » ; la gaine de R1
  // (jusqu'à 6 px) en faisait des traits de 10 px
  vec3 traitFin(float dPx, vec3 coreCol, vec3 col) {
    float core = 1.0 - smoothstep(0.0, 1.0, dPx);
    float sheath = 1.0 - smoothstep(1.0, 2.0, dPx);
    return coreCol * core + col * 0.7 * max(sheath - core, 0.0);
  }
  const vec3 WARM = vec3(1.25, 1.12, 1.0);
`;
// LES BARRES EN PIXELS D'ÉCRAN. Leurs extrémités sont projetées sur le CPU
// à chaque image avec la vraie caméra (dérive comprise) : uBarPx[k] =
// (x0, x1, y, couleur) en pixels du tampon. La distance d'un pixel à une barre
// est donc directement en pixels (R1), et le même tableau sert au ciel (les
// barres) et au sol (leurs reflets). DÉROULÉ en JS : sur le GPU du Pi, un
// tableau d'uniforms indexé par une variable de boucle est lu par l'unité de
// texture, à chaque pixel et pour chaque élément.
const BARS_CHUNK = `
  uniform highp vec4 uBarPx[${N_BARS}];   // x0, x1, y (pixels du tampon), couleur
  uniform highp float uHorizonPx;
  uniform vec3 uBarColA;
  uniform vec3 uBarColB;
  uniform float uBarsWhite;
  vec3 barCol(float flag) { return mix(flag > 0.5 ? uBarColB : uBarColA, vec3(1.0), uBarsWhite); }
`;

function floorVertex() {
  return `
  precision highp float;
  uniform float uDunes;
  varying vec3 vWorld;
  varying vec3 vNormal;
  varying vec4 vClip;
  // les dunes et leur gradient analytique (R5 : normales calculées)
  vec3 dunes(vec2 p) {
    float fade = exp(max(0.0, -p.y - 60.0) / -90.0);
    float a = 0.21 * p.x + 0.13 * p.y, b = 0.37 * p.y - 0.11 * p.x + 1.3, c = 0.07 * p.x + 0.05 * p.y + 2.1;
    float A = ${DUNE_AMP.toFixed(2)} * fade * uDunes;
    float h = A * (0.55 * sin(a) + 0.30 * sin(b) + 0.35 * sin(c));
    float hx = A * (0.55 * 0.21 * cos(a) - 0.30 * 0.11 * cos(b) + 0.35 * 0.07 * cos(c));
    float hz = A * (0.55 * 0.13 * cos(a) + 0.30 * 0.37 * cos(b) + 0.35 * 0.05 * cos(c));
    return vec3(h, hx, hz);
  }
  void main() {
    vec3 d = dunes(position.xz);
    vec3 p = vec3(position.x, d.x, position.z);
    vWorld = p;
    vNormal = normalize(vec3(-d.y, 1.0, -d.z));
    vClip = projectionMatrix * viewMatrix * vec4(p, 1.0);
    gl_Position = vClip;
  }
  `;
}

function floorFragment(NW) {
  return `
  // PRÉCISION : mediump par défaut, HIGHP pour les seules coordonnées du
  // monde (jusqu'à 600 unités : en fp16, fract() d'une coordonnée de 600 n'a
  // plus que 0,25 de résolution et la grille casse) et les pixels d'écran.
  precision mediump float;
  uniform highp vec4 uWaves[${NW}];     // centre x, centre z, rayon, force
  uniform vec3 uAccent;
  uniform vec3 uGridCol;
  uniform float uGrid;
  uniform float uGridFlash;
  uniform float uRefract;
  uniform float uRingLine;
  uniform float uBarsOn;
  uniform float uExposure;
  uniform sampler2D uReflTex;
  uniform highp vec2 uReflMap;          // (pixel x de l'abscisse -REFL_SPAN, 1 / largeur en pixels de 2 REFL_SPAN)
  varying highp vec3 vWorld;
  varying vec3 vNormal;
  varying vec4 vClip;
  ${SAFE_CHUNK}
  ${LINE_CHUNK}
  ${BARS_CHUNK}
  // une famille de traits de grille : profil 1 à 1,5 px (coeur 1,25 px) ; quand
  // l'espacement passe sous 3 px (empreinte > 1/3 de maille), fondu vers une
  // densité moyenne TRÈS SOMBRE sur une bande de transition (4 -> 2,5 px)
  float famille(highp float coord, float fwc) {
    float dl = abs(fract(coord / ${CELL.toFixed(1)} + 0.5) - 0.5) * ${CELL.toFixed(1)};
    float line = 1.0 - smoothstep(0.75, 1.75, dl / fwc);
    float k = smoothstep(0.25, 0.40, fwc / ${CELL.toFixed(1)});
    return mix(line, 0.04, k);
  }
  void main() {
    highp vec2 p = vWorld.xz;
    vec2 fw = max(fwidth(p), vec2(1e-4));
    float pw = 0.5 * (fw.x + fw.y);
    vec2 off = vec2(0.0);
    vec3 ring = vec3(0.0);
    float crest = 0.0;
    // L'ONDE, LOCALISÉE À LA CRÊTE : c(r) = exp(-((r - R) / W)^2), et le
    // déplacement radial = force x c'(r) x amplitude - la dérivée pousse
    // devant la crête et tire derrière. À plus de 3 W : RIEN (coupé net, la
    // grille y est parfaitement droite). La largeur ne grandit plus avec le
    // rayon : une ride qui traverse une grille au repos, pas une loupe.
    ${Array.from({ length: NW }, (_, i) => `{
      highp vec4 w = uWaves[${i}];
      highp vec2 dv = p - w.xy;
      highp float r = length(dv);
      float d = r - w.z;
      if (w.w > 0.0 && (abs(d) < ${(3 * CREST_W).toFixed(2)} || abs(d) < 40.0 * pw)) {
        vec2 dir = dv / max(r, 1e-3);
        float u = d / ${CREST_W.toFixed(2)};
        float c = exp(-u * u);
        float cp = -2.0 * u / ${CREST_W.toFixed(2)} * c;
        float mask = 1.0 - smoothstep(${(2.5 * CREST_W).toFixed(2)}, ${(3 * CREST_W).toFixed(2)}, abs(d));
        off += dir * cp * min(w.w, 1.2) * ${RIPPLE_AMP.toFixed(2)} * mask;
        crest += c * min(w.w, 1.0) * mask;
        // l'anneau : coeur blanc 2 px, gaine d'accent, halo d'accent (R1, R4)
        // (le coeur garde 75 % au moins jusqu'à la fin de la vie de l'onde : il
        // reste plus clair que les coeurs des barres - critère 5)
        float life = min(w.w, 1.0);
        ring += traitCore(1.0 - smoothstep(0.5, 1.5, abs(d) / pw), 1.0 - smoothstep(2.5, 5.5, abs(d) / pw), abs(d) / pw, WARM * ${(RING_CORE / 1.25).toFixed(3)} * (0.75 + 0.25 * life), uAccent * life, 1.0);
      }
    }`).join('\n')}
    // LA GRILLE : deux familles de traits, décalées par la ride
    highp vec2 q = p - off * uRefract;
    float g = max(famille(q.x, fw.x), famille(q.y, fw.y));
    // +40 % sous la crête ; le flash du drop la monte sans lui donner de coeur blanc
    float gridI = uGrid * (1.0 + 0.4 * min(crest, 1.0) + 1.0 * uGridFlash);
    highp vec3 rel = vWorld - cameraPosition;
    float dist = length(rel.xz);
    vec3 col = uGridCol * g * gridI * exp(-dist / 400.0);
    // LE REFLET : pour chaque barre, une TRAÎNÉE VERTICALE sous elle, aussi
    // large qu'elle, qui part de l'horizon et s'éteint vers la caméra sur 25 %
    // de la hauteur du sol - en pixels d'écran, lue au point DÉCALÉ par la
    // ride (l'empreinte du pixel convertit le décalage du sol en pixels)
    if (uBarsOn > 0.5) {
      highp float sx = gl_FragCoord.x - off.x * uRefract / fw.x;
      highp float sy = gl_FragCoord.y + off.y * uRefract / fw.y;
      float below = uHorizonPx - sy;
      // au-delà de 4 longueurs, exp(-4) : le reflet est éteint, on ne le calcule pas
      if (below > 0.0 && below < ${(4 * REFL_LEN).toFixed(2)} * uHorizonPx) {
        float v = exp(-below / (${REFL_LEN.toFixed(2)} * uHorizonPx));
        // le profil horizontal des traînées : UNE lecture d'une texture 1D bâtie
        // une fois (les barres sont fixes sur leur plan), r = poids de la couleur
        // A, g = poids de la couleur B ; sx est ramené à l'abscisse du plan des
        // barres par deux points projetés à chaque image (dérive comprise).
        // Sept barres évaluées par pixel coûtaient ~5 ms sur le Pi.
        vec4 t = texture2D(uReflTex, vec2((sx - uReflMap.x) * uReflMap.y, 0.5));
        col += (barCol(0.0) * t.r + barCol(1.0) * t.g) * v * ${REFL_GAIN.toFixed(2)};
      }
    }
    col += ring * uRingLine;
    gl_FragColor = vec4(col * uExposure * safeAtt(vClip.xy / vClip.w), 1.0);
  }
  `;
}

// LE CIEL : un quad PLEIN ÉCRAN, dessiné en premier, sans profondeur. Les
// barres y sont des segments en distance de pixels (R1 : coeur 2 px blanc,
// gaine 6 px couleur, halo exp(-d / 14) x 0,35 ramené à zéro entre 4 et 6
// largeurs de halo, 56 -> 84 px) : aucun bord de quad nulle part. Au-dessus,
// le noir pur (R3), avec seulement une lueur de la couleur des barres juste
// au-dessus de l'horizon (luminance <= 0,05). Le sol, opaque, recouvre le
// bas. (La version précédente dessinait les barres sur un quad du monde :
// halo trop court et bords de quad visibles en rectangles gris-bleu.)
// le quad ne couvre que le ciel (de l'horizon au haut de l'image) : sous
// l'horizon le sol le recouvrirait de toute façon, et chaque pixel du ciel
// évalue les 7 barres
const SKY_VERTEX = `
  uniform highp float uHorizonNdc;
  void main() {
    float y0 = max(uHorizonNdc - 0.02, -1.0);
    gl_Position = vec4(position.x, mix(y0, 1.0, position.y * 0.5 + 0.5), 1.0, 1.0);
  }
`;
const SKY_FRAGMENT = `
  precision mediump float;
  uniform float uBarsOn;
  uniform float uBarPulse;
  uniform float uExposure;
  uniform vec3 uGlowCol;
  ${LINE_CHUNK}
  ${BARS_CHUNK}
  vec3 barre(highp vec2 f, highp vec4 b) {
    highp float dx = max(max(b.x - f.x, f.x - b.y), 0.0);
    highp float dy = abs(f.y - b.z);
    if (dx > ${(6 * BAR_HALO_PX).toFixed(1)} || dy > ${(6 * BAR_HALO_PX).toFixed(1)}) return vec3(0.0);
    float d = length(vec2(dx, dy));
    float core = 1.0 - smoothstep(0.5, 1.5, d);
    float sheath = 1.0 - smoothstep(4.5, 7.5, d);
    float halo = exp(-d / ${BAR_HALO_PX.toFixed(1)}) * 0.35 * (1.0 - smoothstep(${(4 * BAR_HALO_PX).toFixed(1)}, ${(6 * BAR_HALO_PX).toFixed(1)}, d));
    vec3 c = barCol(b.w);
    return vec3(${BAR_CORE.toFixed(2)}) * core + c * 0.7 * max(sheath - core, 0.0) + c * halo * (1.0 - sheath);
  }
  uniform highp float uBarTopPx;          // le haut de la plus haute barre + son halo
  void main() {
    highp vec2 f = gl_FragCoord.xy;
    vec3 col = vec3(0.0);
    // au-dessus des barres et de leur halo : rien à évaluer (noir, et la lueur)
    if (f.y < uBarTopPx) {
      ${Array.from({ length: N_BARS }, (_, k) => `col += barre(f, uBarPx[${k}]);`).join('\n      ')}
    }
    col *= uBarsOn * uBarPulse;
    float up = f.y - uHorizonPx;
    if (up > 0.0) col += uGlowCol * ${GLOW_MAX.toFixed(3)} * exp(-up / (0.05 * uHorizonPx)) * uBarsOn;
    gl_FragColor = vec4(col * uExposure, 1.0);
  }
`;

function dustVertex(NW) {
  return `
  precision highp float;
  attribute float aSeed;
  uniform vec4 uWaves[${NW}];
  uniform float uDunes;
  uniform float uSize;
  varying float vLift;
  varying float vSeed;
  varying vec4 vClip;
  void main() {
    vec2 p = position.xz;
    float ground = 0.0;
    if (uDunes > 0.001) {
      float fade = exp(max(0.0, -p.y - 60.0) / -90.0);
      float a = 0.21 * p.x + 0.13 * p.y, b = 0.37 * p.y - 0.11 * p.x + 1.3, c = 0.07 * p.x + 0.05 * p.y + 2.1;
      ground = ${DUNE_AMP.toFixed(2)} * fade * uDunes * (0.55 * sin(a) + 0.30 * sin(b) + 0.35 * sin(c));
    }
    // l'enveloppe : montée raide devant la crête, retombée longue derrière
    float lift = 0.0;
    ${Array.from({ length: NW }, (_, i) => `{ vec4 w = uWaves[${i}]; if (w.w > 0.0) { float d = length(p - w.xy) - w.z; float s = d >= 0.0 ? ${LIFT_AHEAD.toFixed(2)} : ${LIFT_BEHIND.toFixed(2)}; lift = max(lift, exp(-(d / s) * (d / s)) * min(w.w, 2.0)); } }`).join('\n    ')}
    float hmax = ${LIFT_MAX.toFixed(2)} * (0.4 + 0.6 * fract(aSeed * 7.13));
    vec3 wp = vec3(p.x, ground + lift * hmax, p.y);
    vLift = lift; vSeed = aSeed;
    vClip = projectionMatrix * viewMatrix * vec4(wp, 1.0);
    gl_Position = vClip;
    gl_PointSize = uSize;
  }
  `;
}
const DUST_FRAGMENT = `
  precision mediump float;
  uniform vec3 uAccent;
  uniform float uExposure;
  varying float vLift;
  varying float vSeed;
  varying vec4 vClip;
  ${SAFE_CHUNK}
  void main() {
    // au sol : une poussière à peine visible ; soulevée : la couleur d'accent,
    // sous le seuil de bloom (seuls les coeurs de traits fleurissent, R1)
    float b = 0.10 + 0.55 * min(vLift, 1.0);
    vec3 col = mix(vec3(0.6), uAccent, 0.75) * b * (0.6 + 0.4 * fract(vSeed * 3.7));
    gl_FragColor = vec4(col * uExposure * safeAtt(vClip.xy / vClip.w), 1.0);
  }
`;

// module-level caches: on the Pi a material recompiles on every appearance otherwise
const MATERIALS = new Map();
function safeUniforms() {
  return { uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
}
function materials(NW) {
  if (MATERIALS.has(NW)) return MATERIALS.get(NW);
  const waves = { value: Array.from({ length: NW }, () => new THREE.Vector4(0, 0, 0, 0)) };
  const accent = { value: new THREE.Color(1, 0.2, 0.6) }, gridCol = { value: new THREE.Color(0.4, 0.1, 0.25) };
  const exposure = { value: 1 }, barsWhite = { value: 0 }, barsOn = { value: 1 }, dunesU = { value: 0 };
  const barPx = { value: Array.from({ length: N_BARS }, () => new THREE.Vector4(-1e4, -1e4, -1e4, 0)) };
  const reflTex = { value: null }, reflMap = { value: new THREE.Vector2(0, 1 / 1000) };
  const horizonPx = { value: 0 }, horizonNdc = { value: 0.5 }, barTopPx = { value: 1e5 }, glowCol = { value: new THREE.Color(0.5, 0.3, 0.6) };
  const barA = { value: new THREE.Color(1, 0.2, 0.6) }, barB = { value: new THREE.Color(0.2, 0.6, 1) };
  const barsU = { uBarPx: barPx, uHorizonPx: horizonPx, uBarColA: barA, uBarColB: barB, uBarsWhite: barsWhite };
  const floor = new THREE.ShaderMaterial({
    uniforms: Object.assign({ uWaves: waves, uAccent: accent, uGridCol: gridCol, uGrid: { value: GRID_BASE }, uGridFlash: { value: 0 }, uRefract: { value: REFRACT }, uRingLine: { value: 1 }, uBarsOn: barsOn, uExposure: exposure, uDunes: dunesU, uReflTex: reflTex, uReflMap: reflMap }, barsU, safeUniforms()),
    vertexShader: floorVertex(), fragmentShader: floorFragment(NW)
  });
  const sky = new THREE.ShaderMaterial({
    uniforms: Object.assign({ uBarsOn: barsOn, uBarPulse: { value: 1 }, uExposure: exposure, uGlowCol: glowCol, uHorizonNdc: horizonNdc, uBarTopPx: barTopPx }, barsU),
    vertexShader: SKY_VERTEX, fragmentShader: SKY_FRAGMENT, depthTest: false, depthWrite: false
  });
  const dust = new THREE.ShaderMaterial({
    uniforms: Object.assign({ uWaves: waves, uAccent: accent, uExposure: exposure, uDunes: dunesU, uSize: { value: 1 } }, safeUniforms()),
    vertexShader: dustVertex(NW), fragmentShader: DUST_FRAGMENT,
    transparent: true, blending: THREE.AdditiveBlending, depthWrite: false
  });
  const m = { floor, sky, dust, U: { waves, accent, gridCol, exposure, barsWhite, barsOn, dunes: dunesU, barPx, horizonPx, horizonNdc, barTopPx, glowCol, reflTex, reflMap, barA, barB, grid: floor.uniforms.uGrid, gridFlash: floor.uniforms.uGridFlash, refract: floor.uniforms.uRefract, ringLine: floor.uniforms.uRingLine, barPulse: sky.uniforms.uBarPulse } };
  MATERIALS.set(NW, m);
  return m;
}

// LE SOL : un trapèze qui suit le champ, les rangs espacés RÉGULIÈREMENT À
// L'ÉCRAN (même hauteur en pixels), du bas de l'image jusqu'à 600 unités.
// Une première version espaçait 220 rangs en exponentielle de la profondeur :
// près de l'horizon, des centaines de rangs tenaient dans quelques pixels,
// et le GPU du Pi passait 16 ms sur des triangles plus petits qu'un pixel -
// même avec un matériau vide. Les rangs n'ont rien à porter au loin (la
// grille, les ondes et le miroir sont par pixel) : seules les dunes ont
// besoin de sommets, et elles s'éteignent au-delà de 60 unités.
function floorGeometry(rows, cols, aspect) {
  const pos = new Float32Array(rows * cols * 3), idx = [];
  // la largeur : le champ RÉEL (+5 % et la dérive), pas un champ de 3,4:1
  // pour tous - sinon, à l'horizon, chaque triangle débordait de mille
  // unités de chaque côté et le GPU du Pi les découpait à grands frais
  const tanH = Math.tan(THREE.MathUtils.degToRad(FOV / 2)) * aspect * 1.05;
  const tanV = Math.tan(THREE.MathUtils.degToRad(FOV / 2));
  // l'angle sous l'horizon d'un rang : du bas de l'image (pitch + fov/2)
  // jusqu'à l'angle où le sol atteint FLOOR_FAR
  const aBottom = PITCH + Math.atan(tanV * 1.08), aFar = Math.atan(CAM_H / FLOOR_FAR);
  for (let j = 0; j < rows; j++) {
    const u = j / (rows - 1);
    // régulier en tangente d'écran (à peu près la position verticale à l'écran)
    const tb = Math.tan(aBottom - PITCH), tf = Math.tan(aFar - PITCH);
    const a = PITCH + Math.atan(tb + (tf - tb) * u);
    const z = -CAM_H / Math.tan(a);
    const half = -z * tanH + 2;
    for (let i = 0; i < cols; i++) {
      const o = (j * cols + i) * 3;
      pos[o] = -half + 2 * half * i / (cols - 1); pos[o + 1] = 0; pos[o + 2] = z;
      // (a, a + 1, a + cols) : la face vers le HAUT. Dans l'autre sens le sol
      // tournait le dos à la caméra et n'était pas dessiné du tout
      if (i < cols - 1 && j < rows - 1) { const k = j * cols + i; idx.push(k, k + 1, k + cols, k + 1, k + cols + 1, k + cols); }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setIndex(idx);
  return g;
}

// la caméra de référence de Shockwave (sans la dérive) : sert à poser la
// poussière et à trouver le point du sol sous la pochette
function cameraDeReference(aspect) {
  const cam = new THREE.PerspectiveCamera(FOV, aspect, 0.1, FAR);
  cam.position.set(0, CAM_H, 0);
  cam.lookAt(0, CAM_H - Math.tan(PITCH) * 10, -10);
  cam.updateMatrixWorld(true); cam.updateProjectionMatrix();
  return cam;
}
// le point du sol vu à (ndcX, ndcY), ou null au-dessus de l'horizon
const _ray = new THREE.Vector3();
export function solSous(cam, ndcX, ndcY) {
  _ray.set(ndcX, ndcY, 0.5).unproject(cam).sub(cam.position).normalize();
  if (_ray.y >= -1e-4) return null;
  const t = -cam.position.y / _ray.y;
  return new THREE.Vector3(cam.position.x + _ray.x * t, 0, cam.position.z + _ray.z * t);
}

// ------------------------------------------------------------- le visualiseur

export class ShockwaveVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = TIER[o.qualityTier] ? o.qualityTier : 1;
    this._layout = TIER[this._tier];
    this._ringDebug = urlParam('shockring') === '1';
    this._dustOff = urlParam('shockdust') === '0';
    this._probe = urlParam('shockprobe') === '1';
    // bloom 0,45 : à 0,6, le halo des coeurs des barres laissait 3/255 dans le ciel à 120 px (R3 : noir pur)
    this._post = new VdjPost({ bloomStrength: 0.45, bloomThreshold: 0.78, grain: 0.015, vignette: 0.4 });
    this._comfort = new VdjComfort();
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene; this._camera = camera;
    const L = this._layout, NW = L.waves;
    // la caméra : légère plongée ; far repoussé jusqu'à l'horizon
    // (reinitialiserCamera la remet avant le visualiseur suivant)
    camera.fov = FOV; camera.far = FAR; camera.near = 0.1;
    camera.position.set(0, CAM_H, 0);
    camera.lookAt(0, CAM_H - Math.tan(PITCH) * 10, -10);
    camera.updateProjectionMatrix();
    const M = this._m = materials(NW);
    this._U = M.U;
    this._group = new THREE.Group();
    scene.add(this._group);
    const aspectF = safeZoneUniforms.uAspect.value || camera.aspect || 1.78;
    this._floor = new THREE.Mesh(floorGeometry(L.rows, L.cols, aspectF), M.floor);
    this._floorAspect = aspectF;
    this._floor.frustumCulled = false; this._floor.renderOrder = 0;
    this._group.add(this._floor);
    // le ciel plein écran (les barres), dessiné AVANT le sol ; ses barres sont
    // projetées en pixels à chaque image avec la vraie caméra (dérive comprise)
    this._barsMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), M.sky);
    this._barsMesh.frustumCulled = false; this._barsMesh.renderOrder = -10;
    const self = this, _sz = new THREE.Vector2(), _a = new THREE.Vector3(), _b = new THREE.Vector3();
    this._barsMesh.onBeforeRender = function (renderer, sc, cam) {
      renderer.getDrawingBufferSize(_sz);
      const W = _sz.x, H = _sz.y, U = self._U;
      cam.updateMatrixWorld();
      for (let k = 0; k < N_BARS; k++) {
        const b = self._barList[k];
        _a.set(b.x - b.half, b.y, BAR_Z).project(cam); _b.set(b.x + b.half, b.y, BAR_Z).project(cam);
        U.barPx.value[k].set((_a.x + 1) * 0.5 * W, (_b.x + 1) * 0.5 * W, (0.5 * (_a.y + _b.y) + 1) * 0.5 * H, b.flag);
      }
      _a.set(cam.position.x, cam.position.y, cam.position.z - 1e5).project(cam);
      U.horizonPx.value = (_a.y + 1) * 0.5 * H;
      U.horizonNdc.value = _a.y;
      // l'abscisse du plan des barres -> pixels : deux points projetés
      _a.set(-REFL_SPAN, 8, BAR_Z).project(cam); _b.set(REFL_SPAN, 8, BAR_Z).project(cam);
      const xa = (_a.x + 1) * 0.5 * W, xb = (_b.x + 1) * 0.5 * W;
      U.reflMap.value.set(xa, 1 / Math.max(1, xb - xa));
      let top = 0; for (const v of U.barPx.value) top = Math.max(top, v.z);
      U.barTopPx.value = top + 6 * BAR_HALO_PX + 2;
    };
    this._group.add(this._barsMesh);
    this._buildBars(rng(77));
    this._buildReflTex();
    // la poussière, répartie uniformément À L'ÉCRAN sous l'horizon
    const aspect = safeZoneUniforms.uAspect.value || camera.aspect || 1.78;
    const ref = cameraDeReference(aspect);
    const r = rng(1234), n = L.dust;
    const dpos = new Float32Array(n * 3), seed = new Float32Array(n);
    const horizonNdc = 0.5;
    for (let k = 0; k < n; k++) {
      let pt = null;
      while (!pt || pt.z < -90) pt = solSous(ref, r() * 2.2 - 1.1, -1.05 + (horizonNdc - 0.06 + 1.05) * Math.pow(r(), 0.8));
      dpos[k * 3] = pt.x; dpos[k * 3 + 1] = 0; dpos[k * 3 + 2] = pt.z;
      seed[k] = r() * 100;
    }
    const dg = new THREE.BufferGeometry();
    dg.setAttribute('position', new THREE.BufferAttribute(dpos, 3));
    dg.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    this._dust = new THREE.Points(dg, M.dust);
    this._dust.frustumCulled = false; this._dust.renderOrder = 2; this._dust.visible = !this._dustOff;
    this._group.add(this._dust);
    this._refCam = ref;

    // l'état
    this._waves = [];                     // { x, z, speed, rMax, strength, born, lifeS }
    this._time = 0; this._paused = false;
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null; this._free = 0; this._beats = 0; this._bars = 0; this._phrases = 0;
    this._secondsBeat = 0.5; this._sinceBeat = 10; this._halfFired = false; this._buildup = 0;
    this._colIdx = 0; this._dunes = 0; this._dunesTarget = 0;
    this._gridFlash = 0; this._barsWhite = 0; this._barPulse = 0;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null; this._lastMode = null; this._lastDropAt = -1e9;
    this._paletteKey = null; this._palette = null;
    this._spawned = { beat: 0, bar: 0, half: 0, drop: 0 };
    this._lumAvg = 0;
    this._applyPalette({ artworkPalette: null });
    if (this._ringDebug) this._spawn(this._centre(), BEAT_R, 1.0, 'debug');
    this._writeWaves();
    if (this._probe && typeof window !== 'undefined') window.__shock = this;
  }

  // le profil horizontal des reflets, une fois : pour chaque barre, plein
  // sous 90 % de sa longueur, puis exp(-e / (8 % de la demi-longueur)) - le
  // même profil que la version par pixel qu'il remplace
  _buildReflTex() {
    const n = REFL_TEX, data = new Uint8Array(n * 4);
    for (let i = 0; i < n; i++) {
      const x = -REFL_SPAN + 2 * REFL_SPAN * (i + 0.5) / n;
      let a = 0, b = 0;
      for (const bar of this._barList) {
        const e = Math.max(Math.abs(x - bar.x) - 0.9 * bar.half, 0);
        const w = Math.exp(-e / (0.08 * bar.half + 0.4));
        if (bar.flag > 0.5) b = Math.max(b, w); else a = Math.max(a, w);
      }
      data[i * 4] = Math.round(a * 255); data[i * 4 + 1] = Math.round(b * 255); data[i * 4 + 3] = 255;
    }
    if (this._reflTex) this._reflTex.dispose();
    const t = new THREE.DataTexture(data, n, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
    t.colorSpace = THREE.NoColorSpace; t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
    t.generateMipmaps = false; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; t.needsUpdate = true;
    this._reflTex = t; this._U.reflTex.value = t;
  }

  _buildBars(r) {
    this._barList = [];
    // R6 : sous les zones d'exclusion du haut (l'horizon est à +0,5, la plus
    // haute barre à ~+0,70 en NDC, les boutons commencent à +0,76)
    for (let k = 0; k < N_BARS; k++) {
      const x = (k - (N_BARS - 1) / 2) * 40 + (r() - 0.5) * 14;
      const y = 3.5 + 13 * r();
      this._barList[k] = { x, y, half: 7 + 10 * r(), flag: k % 2 };
    }
  }

  _applyPalette(profile) {
    const pal = profile.artworkPalette;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff2bd6, 0x2bffea, 0xffb02b];
    const key = v.map(String).join('|');
    if (key === this._paletteKey) return;
    this._paletteKey = key;
    this._palette = v.map((c) => vdjColor(c));
    this._setColours();
  }
  _setColours() {
    const P = this._palette, n = P.length;
    const a = P[this._colIdx % n];
    this._U.accent.value.copy(a);
    // R4 : les deux couleurs des barres à 0,25 de teinte au moins
    const ha = { h: 0, s: 0, l: 0 }, hb = { h: 0, s: 0, l: 0 };
    a.getHSL(ha);
    let best = null, bestD = -1;
    for (const c of P) { c.getHSL(hb); const d = Math.min(Math.abs(ha.h - hb.h), 1 - Math.abs(ha.h - hb.h)); if (d > bestD) { bestD = d; best = c; } }
    const b = best.clone();
    if (bestD < 0.25) { b.getHSL(hb); b.setHSL((ha.h + 0.33) % 1, Math.max(0.85, ha.s), 0.55); }
    this._U.barA.value.copy(a); this._U.barB.value.copy(b);
    // la grille : la couleur d'accent ATTÉNUÉE (luminance <= 0,35) ; la lueur
    // de l'horizon : la moyenne des deux couleurs des barres
    this._U.gridCol.value.copy(a).multiplyScalar(GRID_LUM);
    this._U.glowCol.value.copy(a).add(b).multiplyScalar(0.5);
  }

  // le point du sol sous le centre de scène (R19 : plus sous la pochette)
  _centre() {
    const sc = safeZoneUniforms.uStageCenter.value, asp = safeZoneUniforms.uAspect.value || 1.78;
    return solSous(this._refCam, sc.x / asp, Math.min(sc.y, 0.3)) || new THREE.Vector3(0, 0, -14);
  }
  _spawn(at, rMax, strength, kind) {
    const lifeS = LIFE_BEATS * this._secondsBeat;
    const w = { x: at.x, z: at.z, rMax, speed: rMax / lifeS, strength, born: this._time, lifeS, kind };
    this._waves.push(w);
    while (this._waves.length > this._layout.waves) this._waves.shift();
    if (kind in this._spawned) this._spawned[kind]++;
  }
  _writeWaves() {
    const arr = this._U.waves.value;
    for (let i = 0; i < arr.length; i++) {
      const w = this._waves[i];
      if (!w) { arr[i].set(0, 0, 0, 0); continue; }
      const t = this._ringDebug && w.kind === 'debug' ? 0.5 * w.lifeS : this._time - w.born;
      const u = clamp01(t / w.lifeS);
      arr[i].set(w.x, w.z, w.speed * t, u >= 1 ? 0 : w.strength * Math.pow(1 - u, 1.5));
    }
    if (!this._ringDebug) this._waves = this._waves.filter((w) => this._time - w.born < w.lifeS);
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) { this._applyPalette(profile); this._dropIdx = 0; this._lastNowMs = null; }

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._paused = profile.playbackState !== 'PLAYING';
    this._applyPalette(profile);
    // pause : tout gèle (les ondes s'arrêtent où elles sont), jamais noir
    if (this._paused || this._ringDebug) { this._post.update(1); this._writeWaves(); return; }
    this._time += dt;
    const asp = safeZoneUniforms.uAspect.value;
    if (asp && Math.abs(asp - this._floorAspect) > 0.02) {
      // l'écran a changé de forme : le sol suit (rare - un redimensionnement)
      this._floor.geometry.dispose();
      this._floor.geometry = floorGeometry(this._layout.rows, this._layout.cols, asp);
      this._floorAspect = asp;
    }
    const director = profile.director;
    // repli sans BPM : 120
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / THREE.MathUtils.clamp(bpm, 60, 200) : 0.5;
    const beatS = this._secondsBeat;

    let beat = false, bar = false, phrase = false;
    const bp = profile.beatPhase;
    if (bp != null) { if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true; this._phaseBeat = bp; }
    else { this._free += dt; if (this._free >= beatS) { this._free -= beatS; beat = true; } }
    const barPhase = profile.barPhase;
    if (barPhase != null) { if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhase; }
    else if (beat && (this._beats + 1) % 4 === 0) bar = true;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else if (beat && (this._beats + 1) % 16 === 0) phrase = true;
    if (beat) { this._beats++; this._sinceBeat = 0; this._halfFired = false; }
    if (bar) this._bars++;
    this._sinceBeat += dt;

    // la montée : un drop connu qui approche, ou le segment « build »
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._buildup += (build - this._buildup) * (1 - Math.exp(-dt / 0.8));

    // DROP : onde géante, la grille flashe (au plus 3 fois par seconde),
    // les barres passent au blanc
    let drop = this._tickDrops(profile);
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
    this._lastMode = director ? director.mode : null;
    if (drop && this._time - this._lastDropAt > 1) {
      this._lastDropAt = this._time;
      this._spawn(this._centre(), DROP_R, 2.2, 'drop');
      if (this._comfort.requestFlash(this._time)) this._gridFlash = 1;
      this._barsWhite = 1;
    }
    const low = director && director.hasReliableGrid && director.bands ? clamp01(director.bands.low) : 0.6;
    // TEMPS : une onde depuis le centre ; MONTÉE : à la croche, de plus en plus faibles
    if (beat) {
      const s = this._buildup > 0.3 ? 0.75 - 0.5 * this._buildup : 0.7 + 0.5 * low;
      this._spawn(this._centre(), BEAT_R, s, 'beat');
    }
    if (this._buildup > 0.3 && !this._halfFired && this._sinceBeat >= beatS / 2) {
      this._halfFired = true;
      this._spawn(this._centre(), BEAT_R, 0.6 - 0.45 * this._buildup, 'half');
    }
    // MESURE : une onde depuis un point aléatoire de l'horizon
    if (bar) {
      const x = (Math.random() - 0.5) * 120, z = -70 - Math.random() * 50;
      this._spawn(new THREE.Vector3(x, 0, z), BAR_R, 1.0, 'bar');
    }
    // PHRASE : couleur suivante, GRID <-> DUNES
    if (phrase) { this._phrases++; this._colIdx++; this._setColours(); this._dunesTarget = 1 - this._dunesTarget; }
    this._dunes += (this._dunesTarget - this._dunes) * (1 - Math.exp(-dt / (0.35 * beatS)));
    this._writeWaves();

    // la grille : repos sous le bloom, flash du drop qui retombe en 120 ms
    this._gridFlash *= Math.exp(-dt / GRID_FLASH_TAU);
    this._barsWhite = Math.max(0, this._barsWhite - dt / (BARS_WHITE_BEATS * beatS));
    this._barPulse = beat ? 1 : this._barPulse * Math.exp(-dt / 0.15);
    const U = this._U;
    U.grid.value = GRID_BASE;
    U.gridFlash.value = this._gridFlash;
    U.barsWhite.value = this._barsWhite;
    U.barPulse.value = 0.8 + 0.25 * this._barPulse;
    U.dunes.value = this._dunes;
    // R10 : la luminance moyenne sous 0,45 (estimation : la grille et ses crêtes)
    const est = 0.05 + 0.25 * this._gridFlash + 0.03 * this._waves.length;
    U.exposure.value = this._comfort.measure(est, dt);
    this._post.update(vdjBeatChroma(bp != null ? bp : this._free / beatS, beatS));
  }

  getProbe() {
    return { barPx: this._U.barPx.value.map((v) => v.toArray()), horizonPx: this._U.horizonPx.value, tier: this._tier, waves: this._waves.length, spawned: Object.assign({}, this._spawned), beats: this._beats, bars: this._bars, phrases: this._phrases, dunes: this._dunes, gridFlash: this._gridFlash, barsWhite: this._barsWhite, buildup: this._buildup, flashes: this._comfort._flashes.length, centre: this._centre().toArray() };
  }

  dispose() {
    if (this._scene) this._scene.remove(this._group);
    if (this._floor) this._floor.geometry.dispose();
    if (this._barsMesh) this._barsMesh.geometry.dispose();
    if (this._reflTex) { this._reflTex.dispose(); this._reflTex = null; this._U.reflTex.value = null; }
    if (this._dust) this._dust.geometry.dispose();
    for (const v of this._U.waves.value) v.set(0, 0, 0, 0);
    if (typeof window !== 'undefined' && window.__shock === this) window.__shock = null;
    this._scene = null;
  }
}
